// Administration › Assistance IA : Claude (Anthropic), modèle local ou aucune. La clé n'est jamais réaffichée.
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PlugZap, Save } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { Alerte, Badge, Bouton, Champ, ChampChoix, Chargement, EtatErreur, Panneau, useNotifier } from '../../components/ui.tsx';

interface Reponse {
  reglage: { provider: string; model: string | null; cleRenseignee: boolean; cleFin: string | null; baseUrl: string | null; documentsAutorises: boolean; majLe: string } | null;
  etat: { fournisseur: string; disponible: boolean; modele: string | null; source: string; raison?: string; documentsClientsAutorises: boolean };
  modeles: { id: string; libelle: string }[]; modeleDefaut: string; coffre: boolean;
}

export function OngletIa() {
  const q = useQuery({ queryKey: ['admin-ia'], queryFn: () => api.get<Reponse>('/api/admin/ia') });
  const qc = useQueryClient();
  const notifier = useNotifier();
  const [f, setF] = useState({ provider: 'anthropic', model: '', apiKey: '', baseUrl: '', documentsAutorises: false });
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [test, setTest] = useState<{ ok: boolean; texte: string } | null>(null);

  useEffect(() => {
    if (!q.data) return;
    const r = q.data.reglage;
    setF({ provider: r?.provider ?? 'anthropic', model: r?.model ?? q.data.modeleDefaut, apiKey: '', baseUrl: r?.baseUrl ?? '', documentsAutorises: r?.documentsAutorises ?? false });
  }, [q.data]);

  if (q.isLoading) return <Chargement />;
  if (q.isError) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const d = q.data!;

  const enregistrer = async () => {
    setErreur(null); setEnvoi(true); setTest(null);
    try {
      await api.put('/api/admin/ia', { ...f, apiKey: f.apiKey || null, model: f.model || null, baseUrl: f.baseUrl || null });
      setF({ ...f, apiKey: '' });
      notifier('Assistance IA enregistrée.');
      await qc.invalidateQueries({ queryKey: ['admin-ia'] });
      await qc.invalidateQueries({ queryKey: ['etat'] });
    } catch (e) { setErreur(messageErreur(e)); } finally { setEnvoi(false); }
  };
  const tester = async () => {
    setTest(null); setEnvoi(true);
    try {
      const r = await api.post<{ ok: boolean; dureeMs: number }>('/api/admin/ia/tester');
      setTest({ ok: r.ok, texte: r.ok ? `Connexion opérationnelle (réponse en ${(r.dureeMs / 1000).toFixed(1)} s).` : 'Le service a répondu, mais pas comme attendu : vérifiez le modèle choisi.' });
    } catch (e) { setTest({ ok: false, texte: messageErreur(e) }); } finally { setEnvoi(false); }
  };

  return (
    <Panneau titre="Assistance IA" actions={d.etat.disponible ? <Badge ton="succes">Active — {d.etat.modele}</Badge> : <Badge ton="neutre">Inactive</Badge>}>
      <div className="pile" style={{ maxWidth: 720 }}>
        <p className="petit secondaire">L’assistance propose des contrôles de diagnostic et repère des exigences dans les pièces d’un dossier,
          toujours avec leur source ; chaque proposition est marquée et doit être validée par une personne. Sans assistance, tout le reste fonctionne.</p>
        {!d.coffre && <Alerte ton="danger" titre="Coffre non configuré">La clé maîtresse de l’installation est absente : aucune clé ne peut être enregistrée.</Alerte>}
        {d.etat.raison && !d.etat.disponible && <Alerte ton="ambre">{d.etat.raison}</Alerte>}
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <ChampChoix libelle="Service" value={f.provider} onChange={(e) => setF({ ...f, provider: e.target.value })}
          options={[{ valeur: 'anthropic', libelle: 'Claude (Anthropic)' }, { valeur: 'openai_compatible', libelle: 'Modèle installé en interne (compatible OpenAI)' }, { valeur: 'aucun', libelle: 'Aucune assistance' }]} />
        {f.provider === 'anthropic' && (
          <>
            <ChampChoix libelle="Modèle" value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} options={d.modeles.map((m) => ({ valeur: m.id, libelle: m.libelle }))} />
            <Champ libelle="Clé API Claude" type="password" autoComplete="off" mono value={f.apiKey} onChange={(e) => setF({ ...f, apiKey: e.target.value })}
              placeholder={d.reglage?.cleRenseignee ? `Clé enregistrée se terminant par …${d.reglage.cleFin ?? ''}` : 'sk-ant-…'}
              aide={<>À créer sur la console Anthropic (console.anthropic.com › API Keys). Laisser vide pour conserver la clé enregistrée. Elle est chiffrée et n’est jamais réaffichée.</>} />
          </>
        )}
        {f.provider === 'openai_compatible' && (
          <div className="grille-form">
            <Champ libelle="Adresse du service" mono value={f.baseUrl} onChange={(e) => setF({ ...f, baseUrl: e.target.value })} placeholder="http://10.0.0.20:11434/v1" />
            <Champ libelle="Modèle" mono value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} />
          </div>
        )}
        {f.provider !== 'aucun' && (
          <label className="case">
            <input type="checkbox" checked={f.documentsAutorises} onChange={(e) => setF({ ...f, documentsAutorises: e.target.checked })} />
            <span><strong>Autoriser l’envoi d’extraits</strong> de la documentation et des pièces de dossier à ce service.
              <span className="petit discret"> Sans cette autorisation, l’assistance ne reçoit aucun document. Pour Claude, les données envoyées par l’API relèvent des conditions commerciales d’Anthropic : à valider avec votre contrat.</span></span>
          </label>
        )}
        {test && <Alerte ton={test.ok ? 'succes' : 'danger'}>{test.texte}</Alerte>}
        <div className="barre-actions">
          <Bouton variante="principal" chargement={envoi} icone={<Save aria-hidden />} onClick={enregistrer}>Enregistrer</Bouton>
          {d.etat.disponible && <Bouton chargement={envoi} icone={<PlugZap aria-hidden />} onClick={tester}>Tester la connexion</Bouton>}
        </div>
      </div>
    </Panneau>
  );
}
